/**
 * POS Payment Success Page
 * ========================
 * 
 * Handles the redirect from Stripe checkout and verifies payment status.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Check,
  Receipt,
  Printer,
  Mail,
  ArrowLeft,
  ShoppingCart,
  Loader2,
  XCircle,
  RefreshCw,
} from "lucide-react";

const POSPaymentSuccessPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const sessionId = searchParams.get("session_id");
  const transactionId = searchParams.get("transaction_id");
  
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [receiptNumber, setReceiptNumber] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const maxPolls = 10;
  const pollInterval = 2000;
  
  const checkPaymentStatus = useCallback(async () => {
    if (!sessionId || !transactionId) {
      setError("Missing payment session information");
      setLoading(false);
      return;
    }
    
    try {
      const res = await fetch(
        `${API}/pos/transactions/${transactionId}/payment-status?session_id=${sessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to check payment status");
      }
      
      const data = await res.json();
      setPaymentStatus(data.payment_status);
      
      if (data.payment_status === "paid") {
        setTransaction(data.transaction);
        setReceiptNumber(data.receipt_number);
        setLoading(false);
        toast.success("Payment successful!");
      } else if (data.status === "expired") {
        setError("Payment session expired");
        setLoading(false);
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
      }
    } catch (err) {
      console.error("Error checking payment status:", err);
      setError(err.message);
      setLoading(false);
    }
  }, [sessionId, transactionId, token]);
  
  useEffect(() => {
    if (loading && pollCount < maxPolls && !error) {
      const timer = setTimeout(checkPaymentStatus, pollCount === 0 ? 0 : pollInterval);
      return () => clearTimeout(timer);
    } else if (pollCount >= maxPolls && loading) {
      setError("Payment verification timed out. Please check your transactions.");
      setLoading(false);
    }
  }, [loading, pollCount, error, checkPaymentStatus]);
  
  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;
  
  const handlePrintReceipt = () => {
    // Navigate to receipt print page
    navigate(`/merchant/pos/receipt/${transactionId}`);
  };
  
  const handleEmailReceipt = () => {
    // Navigate to receipt page which has email functionality
    navigate(`/merchant/pos/receipt/${transactionId}`);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin text-indigo-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing Payment...</h2>
          <p className="text-gray-500">Please wait while we verify your payment</p>
          <p className="text-gray-500 text-sm mt-4">
            Attempt {pollCount + 1} of {maxPolls}
          </p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Verification Failed</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => {
                setError(null);
                setLoading(true);
                setPollCount(0);
              }}
              className="border-gray-200"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
            <Link to="/merchant/pos">
              <Button className="bg-indigo-600 hover:bg-indigo-700">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to POS
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
            <Check className="h-10 w-10 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
          <p className="text-gray-500">Transaction completed via Stripe</p>
        </div>
        
        {/* Transaction Summary */}
        {transaction && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6 mb-6">
            <div className="text-center mb-6">
              <p className="text-4xl font-bold text-gray-900 mb-2">
                {formatPrice(transaction.totals?.total || 0)}
              </p>
              <p className="text-gray-500">
                Receipt: <span className="text-indigo-600">{receiptNumber}</span>
              </p>
            </div>
            
            {/* Items Summary */}
            <div className="border-t border-gray-200 pt-4 mb-4">
              <p className="text-sm text-gray-500 mb-3">Items Purchased</p>
              <div className="space-y-2">
                {transaction.items?.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-gray-900">
                      {item.quantity}x {item.name}
                    </span>
                    <span className="text-gray-600">
                      {formatPrice(item.total_amount)}
                    </span>
                  </div>
                ))}
                {transaction.items?.length > 5 && (
                  <p className="text-xs text-gray-500">
                    +{transaction.items.length - 5} more items
                  </p>
                )}
              </div>
            </div>
            
            {/* Totals */}
            <div className="border-t border-gray-200 pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="text-gray-900">{formatPrice(transaction.totals?.subtotal || 0)}</span>
              </div>
              {transaction.totals?.discount_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Discount</span>
                  <span className="text-emerald-400">-{formatPrice(transaction.totals.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">VAT (20%)</span>
                <span className="text-gray-900">{formatPrice(transaction.totals?.tax_amount || 0)}</span>
              </div>
              <div className="flex justify-between font-semibold pt-2 border-t border-gray-200">
                <span className="text-gray-900">Total Paid</span>
                <span className="text-indigo-400">{formatPrice(transaction.totals?.total || 0)}</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={handlePrintReceipt}
              className="border-gray-200"
              data-testid="print-receipt-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Receipt
            </Button>
            <Button
              variant="outline"
              onClick={handleEmailReceipt}
              className="border-gray-200"
              data-testid="email-receipt-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email Receipt
            </Button>
          </div>
          
          <Link to="/merchant/pos" className="block">
            <Button className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="new-sale-btn">
              <ShoppingCart className="h-4 w-4 mr-2" />
              New Sale
            </Button>
          </Link>
        </div>
        
        {/* Transaction Details */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Transaction ID: {transactionId}
          </p>
          <p className="text-xs text-gray-500">
            {transaction?.transaction_number}
          </p>
        </div>
      </div>
    </div>
  );
};

export default POSPaymentSuccessPage;
